import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { MENUITEMS } from "./Menu";
import styles from "./SidebarMenuItems.module.scss";
import {
  Anchor,
  ArrowRightCircle,
  BarChart,
  Calendar,
  CheckCircle,
  CheckSquare,
  Folder,
  FolderKanban,
  Gift,
  Layers,
  MailQuestion,
  MessageCircle,
  RectangleVerticalIcon,
  Settings as SettingsIcon,
  ShoppingCart,
  StickyNote,
  Store,
  TicketIcon,
  Tv2,
  Users,
  Users2,
  Video,
  Pin,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

export const iconMap = {
  dashboard: Tv2,
  HR: Users,
  "sales-screen": BarChart,
  "google-meet": Video,
  "zoom-meeting": Video,
  Events: Calendar,
  clients: Gift,
  projects: FolderKanban,
  task: CheckCircle,
  "to-do": CheckSquare,
  leads: Layers,
  sales: ShoppingCart,
  store: Store,
  prospects: Anchor,
  notes: StickyNote,
  messages: MessageCircle,
  team: Users2,
  tickets: TicketIcon,
  expenses: ArrowRightCircle,
  reports: RectangleVerticalIcon,
  files: Folder,
  help: MailQuestion,
  settings: SettingsIcon,
};

const SidebarMenuItems = ({ setMainMenu, setNavActive, activeClass }) => {
  const location = useLocation();
  const CurrentPath = location.pathname;

  // `:layout` segmenti URL'den kaldırıldı (LayoutRoutes hem temiz hem legacy
  // formu kayıt ediyor). Sidebar artık doğrudan menüdeki path'i emit eder.
  const toHref = (path = "") => path || "/";

  const { t } = useTranslation();

  // ---------------- PIN STATE ----------------
  const [pinned, setPinned] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pinnedMenuItems") || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("pinnedMenuItems", JSON.stringify(pinned));
  }, [pinned]);

  const getItemKey = (item) =>
    item?.path ? `link:${item.path}` : `sub:${item?.title || ""}`;

  const isPinned = (item) => pinned.includes(getItemKey(item));

  const togglePin = (item) => {
    const key = getItemKey(item);
    setPinned((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [key, ...prev]
    );
  };
  // -------------------------------------------

  const normalize = (path = "") => path.replace(/\/+$/, "");

  const isItemActive = (item) => {
    const curr = normalize(CurrentPath);
    if (item?.type === "link" && item?.path) {
      const base = normalize(item.path);
      return curr.startsWith(base) || curr.includes(base);
    }
    if (item?.type === "sub" && Array.isArray(item.children)) {
      return item.children.some((child) => {
        const p = normalize(child?.path || "");
        return p && (curr.startsWith(p) || curr.includes(p));
      });
    }
    return false;
  };

  const toggletNavActive = (item) => {
    if (window.innerWidth <= 1200) {
      const pageHeader = document.querySelector(".page-header");
      const sidebarWrapper = document.querySelector(".sidebar-wrapper");
      if (pageHeader) pageHeader.className = "page-header close_icon";
      if (sidebarWrapper) sidebarWrapper.className = "sidebar-wrapper close_icon ";
      if (item.type === "sub") {
        if (pageHeader) pageHeader.className = "page-header";
        if (sidebarWrapper) sidebarWrapper.className = "sidebar-wrapper";
      }
    }
    if (!item.active) {
      MENUITEMS.map((a) => {
        a.Items.filter((Items) => {
          if (a.Items.includes(item)) Items.active = false;
          if (!Items.children) return false;
          Items.children.forEach((b) => {
            if (Items.children.includes(item)) b.active = false;
            if (!b.children) return false;
            b.children.forEach((c) => {
              if (b.children.includes(item)) c.active = false;
            });
          });
          return Items;
        });
        return a;
      });
    }
    item.active = !item.active;
    setMainMenu({ mainmenu: MENUITEMS });
  };

  // Tüm top-level öğeleri bul, pinned listesi onları kullanacak
  const allTopItems = MENUITEMS.flatMap((sec) => sec.Items || []);
  const pinnedItems = pinned
    .map((key) => allTopItems.find((i) => getItemKey(i) === key))
    .filter(Boolean); // sıralama: pinleme sırası

  // Bir öğenin “pinned listede” olup olmadığına göre section’da filtreleyeceğiz
  const isItemPinned = (item) => pinned.some((k) => k === getItemKey(item));

  const renderMenuItem = (menuItem, i) => {
    const Icon = iconMap[menuItem.icon];
    const active = isItemActive(menuItem);
    const pinnedHere = isPinned(menuItem);

    return (
      <li
        className={`sidebar-list ${active ? "active" : ""} ${
          pinnedHere ? "pinned" : ""
        }`}
        key={getItemKey(menuItem) + i}
      >
        {/* SUB */}
        {menuItem.type === "sub" ? (
          <a
            href="#"
            className={`sidebar-link sidebar-title ${active ? "active" : ""}`}
            onClick={(event) => {
              event.preventDefault();
              setNavActive(menuItem);
              activeClass(menuItem.active);
            }}
          >
            {Icon && <Icon className={styles.navIcon} />}
            <span className={`sidebar-title-span ${styles.titleSpan}`}>{t(menuItem.title)}</span>

            {/* Pin Button */}
            <button
              className="pin-btn"
              title={pinnedHere ? t("Unpin") : t("Pin")}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                togglePin(menuItem);
              }}
            >
              <Pin fill="#6f6cf3" size={12} color="#6f6cf3" />
            </button>

            <div className="according-menu">
              {menuItem.active ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </div>
          </a>
        ) : null}

        {/* LINK */}
        {menuItem.type === "link" ? (
          <Link
            to={toHref(menuItem.path)}
            className={`sidebar-link sidebar-title link-nav ${
              active ? "active" : ""
            }`}
            onClick={() => toggletNavActive(menuItem)}
          >
            {Icon && <Icon className={styles.navIcon} />}
            <span className={`sidebar-title-span ${styles.titleSpan}`}>{t(menuItem.title)}</span>

            {/* Pin Button */}
            <button
              className="pin-btn"
              title={pinnedHere ? t("Unpin") : t("Pin")}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                togglePin(menuItem);
              }}
            >
              <Pin fill="#6f6cf3" size={12} color="#6f6cf3" />
            </button>
          </Link>
        ) : null}

        {/* CHILDREN — reveal CSS'te (.is-open → max-height/opacity); inline
            display KALDIRILDI (anlık pop → submenu parent'tan önce sıçrıyordu). */}
        {menuItem.children ? (
          <ul
            className={`sidebar-submenu ${
              menuItem?.active || CurrentPath === menuItem?.path ? "is-open" : ""
            }`}
          >
            {menuItem.children.map((childrenItem, index) => {
              return (
                <li key={index}>
                  {childrenItem.type === "sub" ? (
                    <a
                      href="#"
                      className={`${
                        CurrentPath.includes(childrenItem?.title?.toLowerCase())
                          ? "active"
                          : ""
                      }`}
                      onClick={(event) => {
                        event.preventDefault();
                        toggletNavActive(childrenItem);
                      }}
                    >
                      {t(childrenItem.title)}
                      <span className="sub-arrow">
                        <ChevronRight size={12} />
                      </span>
                      <div className="according-menu">
                        {childrenItem.active ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </div>
                    </a>
                  ) : null}

                  {childrenItem.type === "link" ? (
                    <Link
                      to={toHref(childrenItem.path)}
                      className={`${
                        CurrentPath.includes(childrenItem?.title?.toLowerCase())
                          ? "active"
                          : ""
                      }`}
                      onClick={() => toggletNavActive(childrenItem)}
                    >
                      {t(childrenItem.title)}
                    </Link>
                  ) : null}

                  {childrenItem.children ? (
                    <ul
                      className={`nav-sub-childmenu submenu-content ${
                        CurrentPath.includes(
                          childrenItem?.title?.toLowerCase()
                        ) || childrenItem.active
                          ? "is-open"
                          : ""
                      }`}
                    >
                      {childrenItem.children.map((childrenSubItem, key) => (
                        <li key={key}>
                          {childrenSubItem.type === "link" ? (
                            <Link
                              to={toHref(childrenSubItem.path)}
                              className={`${
                                CurrentPath.includes(
                                  childrenSubItem?.title?.toLowerCase()
                                )
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => toggletNavActive(childrenSubItem)}
                            >
                              {t(childrenSubItem.title)}
                            </Link>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </li>
    );
  };

  return (
    <>
      {/* ---- PINNED LIST (GENERAL'İN ÜSTÜNDE) ---- */}
      {pinnedItems.length > 0 && (
        <>
          {pinnedItems.map((mi, i) => renderMenuItem(mi, i))}
          <li className="sidebar-divider">
            <hr className={styles.dividerLine} />
          </li>
        </>
      )}

      {/* ---- NORMAL SECTIONS (PINLENENLER FİLTRELENİR) ---- */}
      {MENUITEMS.map((Item, i) => (
        <Fragment key={i}>
          {Item.Items.filter((it) => !isItemPinned(it)).map((menuItem, idx) =>
            renderMenuItem(menuItem, idx)
          )}
        </Fragment>
      ))}
      {/* pin-btn / divider / pinned stilleri Sidebar.scss'e taşındı (eski inline
          <style> enjeksiyonu KALINTI — her render'da DOM'a stil basıyordu). */}
    </>
  );
};

export default SidebarMenuItems;
